import { bankFileSynthesisSchema, type BankFileSynthesis } from "@hakili/contracts";
import { BANK_FILE_STAGE_LABELS, type BankFileStageCode } from "@hakili/domain-types";
import { formatDate, formatMoney, t } from "@hakili/i18n";
import { Button, SegmentedControl, StatCard, Table } from "@hakili/ui";
import { RefreshCw } from "lucide-react";
import { useState } from "react";

import { DOT_CHIP, DOT_TONES, FileWho, ListCard, ListState, StageDotChip } from "../file-list.js";
import { nextStepOf } from "../stage-words.js";

/**
 * The bank's file as `GET /bank/files` and `/bank/pipeline` answer it, and
 * round `L-0`'s « Dossiers » screen to the drawing (`hakili-bank.jsx`,
 * `DossiersScreen`): the sentence, the four tiles DERIVED from the rows or a
 * dash, the search (live), the status filter, the table with its six columns,
 * the footer sentence.
 *
 * `A-1a` (D-1021-1, D3 §2): the same screen on the kit's parts , `StatCard` for the tiles,
 * `SegmentedControl` for the status, `Table` with its `rowLink` for the list , and the greyed
 * « Exporter » (no door) is no longer drawn (D-1019-2).
 */
export interface FichierBanque {
  readonly id: string;
  readonly reference: string;
  /** `U-3`, `M2`: the solidity grade from the run, re-derived from her acts; `null` where no run stands. */
  readonly grade?: string | null;
  readonly stage: string;
  readonly stageEnteredAt: string;
  readonly requiresComite: boolean;
  readonly archived: boolean;
  readonly quotitePct: number;
  readonly amountFinanced: number;
  readonly submittedAt: string | null;
  /** `L-0`: the client and the vehicle, from the dossier's row (05 §2.6.15: `client_name` and nothing else). */
  readonly clientName: string | null;
  readonly vehicleLabel: string | null;
  /** `L-1`, `M1`: the buyer's own deposit and term, the file's; `null` prints a dash (R81). */
  readonly depositPct: number | null;
  readonly durationMonths: number | null;
  /** `L-5`, `M2` (WF-BNK-24's analyst filter): who carries the file, from the row; `null` before a take. */
  readonly analystUserId: string | null;
  /** `LISTS-DATA`: whole days from the Bamako day received to the door's day, counted by the SERVER; `null` where the door sent none. */
  readonly receivedDaysAgo?: number | null;
  /** `LISTS-DATA`: the count of blocking findings of the file's run; `null` with no register (never 0 for « unknown »). */
  readonly blockingFindings?: number | null;
  /** `LISTS-DATA`: why the file was lost (the typed code) and when; `null` while it stands. */
  readonly lost?: { readonly reason: string; readonly at: string } | null;
  /** `LISTS-DATA`: the refusal the bank recorded (its label) and when; `null` unless the file is refused. */
  readonly refusal?: { readonly label: string; readonly at: string } | null;
  /** `A-1b-2a`: the file's own `figures.availableCapacityAmount` (the bank's read), `null` where the door gives none. */
  readonly availableCapacityAmount?: number | null;
  /** `A-1b-2b` (SYNTHESIS-FIELDS): the typed facts of the synthesis, as the file's own door sends them; `null` where the door sends none or not in its shape. */
  readonly synthesis?: BankFileSynthesis | null;
}

const asString = (value: unknown): string | null => (typeof value === "string" ? value : null);
const asNumber = (value: unknown): number | null => (typeof value === "number" ? value : null);

/** `LISTS-DATA`: the door's `lost { reason, at }`, or `null` for anything that is not that shape. */
const lostFrom = (value: unknown): { readonly reason: string; readonly at: string } | null => {
  if (typeof value !== "object" || value === null) return null;
  const reason = asString((value as Record<string, unknown>)["reason"]);
  const at = asString((value as Record<string, unknown>)["at"]);
  return reason === null || at === null ? null : { reason, at };
};

/** `LISTS-DATA`: the door's `refusal { code, label, at }`: the screen reads the label and the instant. */
const refusalFrom = (value: unknown): { readonly label: string; readonly at: string } | null => {
  if (typeof value !== "object" || value === null) return null;
  const label = asString((value as Record<string, unknown>)["label"]);
  const at = asString((value as Record<string, unknown>)["at"]);
  return label === null || at === null ? null : { label, at };
};

export function fichierFrom(row: unknown): FichierBanque | null {
  if (typeof row !== "object" || row === null) return null;
  const raw = row as Record<string, unknown>;
  const id = asString(raw["id"]);
  const reference = asString(raw["reference"]);
  const stage = asString(raw["stage"]);
  const stageEnteredAt = asString(raw["stageEnteredAt"]);
  const quotitePct = asNumber(raw["quotitePct"]);
  const amountFinanced = asNumber(raw["amountFinanced"]);
  if (
    id === null ||
    reference === null ||
    stage === null ||
    stageEnteredAt === null ||
    quotitePct === null ||
    amountFinanced === null
  ) {
    return null;
  }
  return {
    id,
    reference,
    grade: asString(raw["grade"]),
    stage,
    stageEnteredAt,
    requiresComite: raw["requiresComite"] === true,
    archived: raw["archived"] === true,
    quotitePct,
    amountFinanced,
    submittedAt: asString(raw["submittedAt"]),
    clientName: asString(raw["clientName"]),
    vehicleLabel: asString(raw["vehicleLabel"]),
    depositPct: asNumber(raw["depositPct"]),
    durationMonths: asNumber(raw["durationMonths"]),
    analystUserId: asString(raw["analystUserId"]),
    receivedDaysAgo: asNumber(raw["receivedDaysAgo"]),
    blockingFindings: asNumber(raw["blockingFindings"]),
    lost: lostFrom(raw["lost"]),
    refusal: refusalFrom(raw["refusal"]),
    availableCapacityAmount: (() => {
      const figures = raw["figures"];
      return typeof figures === "object" && figures !== null
        ? asNumber((figures as Record<string, unknown>)["availableCapacityAmount"])
        : null;
    })(),
    synthesis: (() => {
      const parsed = bankFileSynthesisSchema.safeParse(raw["synthesis"]);
      return parsed.success ? parsed.data : null;
    })(),
  };
}

export function fichiersFrom(answer: unknown): FichierBanque[] {
  if (typeof answer !== "object" || answer === null) return [];
  const files = (answer as Record<string, unknown>)["files"];
  if (!Array.isArray(files)) return [];
  return files.map(fichierFrom).filter((file): file is FichierBanque => file !== null);
}

export const DASH = t("common.valeur.tiret");

export const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), t(key));

/** The drawing's status chips: the words are domain-types' (`BANK_FILE_STAGE_LABELS`, one vocabulary since `L-1`'s `M0`), the shades the drawing's. */
const SHADES: ReadonlyMap<string, string> = new Map([
  ["nouveau", "bg-indigo-50 text-indigo-700"],
  ["revue", "bg-sky-50 text-sky-700"],
  ["validation", "bg-violet-50 text-violet-700"],
  ["offre", "bg-amber-50 text-amber-700"],
  ["comite", "bg-violet-50 text-violet-700"],
  ["signature", "bg-cyan-50 text-cyan-700"],
  ["conditions", "bg-teal-50 text-teal-700"],
  ["decaisse", "bg-emerald-50 text-emerald-700"],
  ["retour", "bg-gray-100 text-gray-700"],
  ["refuse", "bg-rose-50 text-rose-700"],
  ["perdu", "bg-gray-100 text-gray-700"],
]);
const STAGE_LABELS = new Map<string, string>(Object.entries(BANK_FILE_STAGE_LABELS));

export function StatusChip({
  stage,
  words,
}: {
  readonly stage: string;
  readonly words?: string | undefined;
}): React.JSX.Element {
  return (
    <span
      className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${SHADES.get(stage) ?? "bg-gray-100 text-gray-600"}`}
      data-testid="bank-statut"
    >
      {/* `L-5`, `M3`: a `perdu` file's chip reads the row's reason (F7 extended); the stage's word otherwise. */}
      {words ?? STAGE_LABELS.get(stage) ?? stage}
    </span>
  );
}

/** The drawing's status sentence per stage, with what a door gives (a date) and nothing more. */
const PHRASE: ReadonlyMap<string, Parameters<typeof t>[0]> = new Map([
  ["nouveau", "bank.phrase.nouveau"],
  ["revue", "bank.phrase.revue"],
  ["validation", "bank.phrase.validation"],
  ["offre", "bank.phrase.offre"],
  ["comite", "bank.phrase.comite"],
  ["signature", "bank.phrase.signature"],
  ["conditions", "bank.phrase.conditions"],
  ["decaisse", "bank.phrase.decaisse"],
  ["retour", "bank.phrase.retour"],
  ["refuse", "bank.phrase.refuse"],
  ["perdu", "bank.phrase.perdu"],
]);

export function phraseOf(file: FichierBanque): string {
  const key = PHRASE.get(file.stage);
  if (key === undefined) return DASH;
  const date = formatDate(
    new Date(file.stage === "nouveau" ? (file.submittedAt ?? file.stageEnteredAt) : file.stageEnteredAt),
  );
  return fill(key, { date });
}

export const initialsOf = (name: string | null): string =>
  name === null
    ? DASH
    : name
        .split(/\s+/u)
        .filter((part) => part !== "")
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");

/** D2's line under the client: « CR-2026-117 · Toyota Corolla » (the product is not repeated on every row). */
export const productOf = (file: FichierBanque): string => file.vehicleLabel ?? DASH;

/**
 * The grade chip (D2 `noteChip`): the run's note through the door (`U-3`, `M2`), a dash where no run stands. The best grades read as
 * settled (the theme's `ok`), the others in the brand tint; amber is for a thing that needs a hand, and a grade is not one (R-13).
 */
export function GradeChip(props: { readonly grade?: string | null | undefined } = {}): React.JSX.Element {
  const grade = props.grade ?? null;
  const tone: keyof typeof DOT_TONES =
    grade === null ? "neutral" : grade === "A" || grade === "A+" ? "ok" : "brand";
  return (
    <span title={t("bank.pipeline.soliditeTitre")} data-testid="bank-note" data-grade={grade ?? "none"}>
      <span className={`${DOT_CHIP} ${DOT_TONES[tone]}`} data-kind="chip-label">
        {grade ?? DASH}
      </span>
    </span>
  );
}

/** Typed stage codes (`BankFileStageCode`): a misspelt stage is a compile error here, not a silent miss in a count. */
const OPEN_STAGES: ReadonlySet<string> = new Set<BankFileStageCode>([
  "nouveau",
  "revue",
  "validation",
  "offre",
  "comite",
  "signature",
  "conditions",
  "retour",
]);
const FUNDED: BankFileStageCode = "decaisse";
const DECIDED: ReadonlySet<string> = new Set<BankFileStageCode>(["decaisse", "refuse"]);
const FILTERS: readonly { readonly id: string; readonly key: Parameters<typeof t>[0] }[] = [
  { id: "tous", key: "bank.dossiers.filtreTous" },
  { id: "actifs", key: "bank.dossiers.filtreActifs" },
  { id: "decaisse", key: "bank.dossiers.filtreDecaisses" },
  { id: "refuse", key: "bank.dossiers.filtreRefuses" },
  { id: "perdu", key: "bank.dossiers.filtreDeclines" },
];

/** The lost-file words of D2 (`b2c142b7`), by the door's typed reason; the date is `formatDate`'s. A reason the screen has no words for keeps the stage's own sentence. */
const LOST_WORDS: ReadonlyMap<string, Parameters<typeof t>[0]> = new Map([
  ["not-retained", "bank.dossiers.perduNonRetenue"],
  ["withdrawn", "bank.dossiers.perduSansSuite"],
  ["client-declined", "bank.dossiers.perduClientDeclined"],
  ["expired", "bank.dossiers.perduExpiree"],
]);

/** « Refusé : capacité de remboursement insuffisante »: D2 prints the motive in lower case after the colon. */
const lowerFirst = (text: string): string => text.charAt(0).toLocaleLowerCase("fr") + text.slice(1);

/** One style for the « Dernier fait » column: no final full stop, whatever a label from the door ends with. */
const withoutFinalStop = (text: string): string => text.replace(/\.\s*$/u, "");

/** D2's « dernier fait »: the stage's gesture while open, « Fonds versés, deux virements » once funded, and for a lost or refused file the door's own facts (the loss's typed reason, the refusal's motive); the stage's sentence where the door sent none. */
const lastFactOf = (file: FichierBanque): string => {
  if (file.stage === "decaisse") return t("bank.dossiers.geste");
  if (OPEN_STAGES.has(file.stage)) return nextStepOf(file.stage);
  if (file.stage === "perdu" && file.lost !== null && file.lost !== undefined) {
    const key = LOST_WORDS.get(file.lost.reason);
    if (key !== undefined) return fill(key, { date: formatDate(new Date(file.lost.at)) });
  }
  if (file.stage === "refuse" && file.refusal !== null && file.refusal !== undefined) {
    return fill("bank.dossiers.refuseMotif", { motif: withoutFinalStop(lowerFirst(file.refusal.label)) });
  }
  return phraseOf(file);
};

export function Dossiers(props: {
  readonly fichiers: readonly FichierBanque[];
  readonly onOuvrir: (file: FichierBanque) => void;
  /** `PIPELINE-STALE-LIST` (A47): the last read of the lists failed. The screen says so, with the Pipeline's own words, and draws no rows and no figures. */
  readonly failure?: { readonly onRetry: () => void };
}): React.JSX.Element {
  const [filter, setFilter] = useState("tous");
  const all = props.fichiers;
  const filtered = all.filter((file) =>
    filter === "tous" ? true : filter === "actifs" ? OPEN_STAGES.has(file.stage) : file.stage === filter,
  );
  const financed = all.filter((file) => file.stage === FUNDED);
  // D2: « décidés » are the files the bank decided, funded or refused; an offer the client let lapse was not the bank's decision.
  const decided = all.filter((file) => DECIDED.has(file.stage)).length;
  const approval = decided === 0 ? DASH : `${String(Math.trunc((financed.length * 100) / decided))}\u202f%`;
  const outstanding = financed.reduce((sum, file) => sum + file.amountFinanced, 0);
  const tiles: [string, string, string][] = [
    [t("bank.dossiers.tuileTotal"), String(all.length), t("bank.dossiers.tuileTotalSous")],
    [t("bank.dossiers.tuileFinances"), String(financed.length), t("bank.dossiers.tuileFinancesSous")],
    [
      t("bank.dossiers.tuileApprobation"),
      approval,
      decided === 0
        ? t("bank.dossiers.tuileApprobationSousAucun")
        : fill(
            decided === 1 ? "bank.dossiers.tuileApprobationSousUn" : "bank.dossiers.tuileApprobationSous",
            {
              n: String(decided),
            },
          ),
    ],
    [
      t("bank.dossiers.tuileEncours"),
      financed.length === 0 ? DASH : formatMoney(outstanding).replace(/\s?FCFA$/u, ""),
      t("bank.dossiers.tuileEncoursSous"),
    ],
  ];
  const heading = (
    <>
      <h1 className="mb-1 mt-2 text-[length:var(--hk-fs-h1)] font-semibold leading-tight tracking-[-0.01em]">
        {t("bank.dossiers.titre")}
      </h1>
      <p className="mb-[18px] max-w-[80ch] text-[length:var(--hk-fs-read)] text-[color:var(--hk-text-2)]">
        {t("bank.dossiers.sousTitre")}
      </p>
    </>
  );
  if (props.failure !== undefined) {
    return (
      <div data-testid="bank-liste">
        {heading}
        <ListState
          kind="error"
          testId="bank-liste-erreur"
          title={t("bank.pipeline.erreurTitre")}
          sentence={t("bank.pipeline.erreurPhrase")}
          action={
            <Button variant="secondary" data-testid="bank-liste-reessayer" onClick={props.failure.onRetry}>
              <span className="inline-flex items-center gap-2">
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                {t("common.action.reessayer")}
              </span>
            </Button>
          }
        />
      </div>
    );
  }
  return (
    <div data-testid="bank-liste">
      {heading}
      <div className="mb-4 grid grid-cols-4 gap-2.5">
        {tiles.map(([label, value, sub]) => (
          <StatCard key={label} label={label} value={value} hint={sub} />
        ))}
      </div>
      <div className="mb-3" data-testid="bank-filtre-statut">
        <SegmentedControl
          label={t("bank.dossiers.filtreStatut")}
          value={filter}
          onChange={setFilter}
          options={FILTERS.map((one) => ({
            value: one.id,
            label: t(one.key),
            testId: `bank-filtre-${one.id}`,
          }))}
        />
      </div>
      {filtered.length === 0 ? (
        <ListState
          kind="empty"
          testId="bank-liste-vide"
          title={t("bank.pipeline.vueVideTitre")}
          sentence={t("bank.pipeline.vueVidePhrase")}
        />
      ) : (
        <ListCard>
          <Table
            columns={[
              {
                key: "client",
                header: t("bank.liste.colDossier"),
                cell: (file: FichierBanque) => (
                  <FileWho
                    initials={initialsOf(file.clientName)}
                    name={file.clientName ?? DASH}
                    line={`${file.reference} · ${productOf(file)}`}
                    testId={`ouvrir-${file.reference}`}
                    lineTestId={`fichier-${file.reference}`}
                  />
                ),
              },
              {
                key: "montant",
                header: t("bank.dossiers.colMontant"),
                align: "right",
                cell: (file: FichierBanque) => (
                  <span className="font-[family-name:var(--hk-mono)]">
                    {formatMoney(file.amountFinanced).replace(/\s?FCFA$/u, "")}
                  </span>
                ),
              },
              {
                key: "note",
                header: t("bank.dossiers.colNote"),
                cell: (file: FichierBanque) => <GradeChip grade={file.grade} />,
              },
              {
                key: "statut",
                header: t("bank.dossiers.colStatut"),
                cell: (file: FichierBanque) => <StageDotChip stage={file.stage} mode="all" />,
              },
              {
                key: "fait",
                header: t("bank.liste.colDernierFait"),
                cell: (file: FichierBanque) => (
                  <span className="text-[color:var(--hk-text-2)]">{lastFactOf(file)}</span>
                ),
              },
            ]}
            rows={filtered}
            rowKey={(file) => file.id}
            rowLink={{
              onOpen: props.onOuvrir,
              label: (file) =>
                fill("bank.liste.ouvrirDossier", { client: file.clientName ?? file.reference }),
            }}
          />
        </ListCard>
      )}
    </div>
  );
}

/** The provider's `vehicleLabel` is the catalog title and carries its year (« Toyota Corolla 2021 »): the year is not printed twice. */
const LABEL_ENDS_WITH_YEAR = /\b(?:19|20)\d{2}$/u;

/**
 * `ARC-3` (D2 `head()`): the dossier head's sub-line parts, « véhicule année · montant · n mois demandés · banque ». What the file does not
 * hold is left out, never a dash or a figure; the year is the offer door's and rides the label only when it has answered. The chef's
 * decision page (D2 frame 62) draws the same parts and adds « soumise par {analyste} ».
 */
export function headSubLineParts(parts: {
  readonly vehicleLabel: string | null;
  readonly vehicleYear: number | null;
  readonly amountFinanced: number;
  readonly requestedMonths: number | null;
  readonly bankName: string | null;
}): readonly string[] {
  return [
    parts.vehicleLabel === null
      ? null
      : parts.vehicleYear === null || LABEL_ENDS_WITH_YEAR.test(parts.vehicleLabel)
        ? parts.vehicleLabel
        : `${parts.vehicleLabel} ${String(parts.vehicleYear)}`,
    formatMoney(parts.amountFinanced),
    parts.requestedMonths === null
      ? null
      : fill("bank.dossier.moisDemandes", { n: String(parts.requestedMonths) }),
    parts.bankName,
  ].filter((one): one is string => one !== null);
}
